/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  Icon,
  MuiBox,
  SelectDropdown,
  Typography,
  Tooltip,
  FormDialog
} from '@kis-technologies/kis-ui';
import { NULL_TICKET_TEMPLATE, NullTicketTemplate } from 'constants/forms';
import { useActions } from 'hooks/useActions';
import {
  selectors as ticketTemplatesSelectors,
  actions as ticketTemplatesActions,
  TicketTemplate
} from 'store/ticketTemplates';
import { selectors as authSelectors } from 'store/auth';
import { selectors as ciSelectors, actions as ciActions } from 'store/clientIntegrations';
import { Products as ProductsApiResponse } from 'store/clientIntegrations/types';
import { toDecimal, toPercentage } from 'utils/number';
import { getSortedList } from 'utils/stringFormat';
import ProductsTable from './components/ProductsTable';
import ProductOverlay from './components/ProductOverlay';
import PriceTypeOverlay from './components/PriceTypeOverlay';
import { KioskTemplateOverlay } from 'views/KiosksView/components/KioskFormOverlay/KioskTemplateOverlay';
import { useStyles } from './styled';
import { ProductsViewProps, ProductsPageData, Product, PriceType, ProductPermissions } from './types';

const ProductsView = ({ setHasUnsavedChanges }: ProductsViewProps) => {
  const admin = useSelector(authSelectors.selectAdmin);
  const clientIntegrations = useSelector(ciSelectors.selectClientIntegrations);
  const paymentSettings = useSelector(ciSelectors.selectPaymentSettings);
  const productsData = useSelector(ciSelectors.selectProducts);
  const templates = useSelector(ticketTemplatesSelectors.selectTicketTemplates);

  const [getClientIntegrations, getPaymentSettings, getProducts, syncProducts] = useActions([
    ciActions.getClientIntegrations,
    ciActions.getPaymentSettings,
    ciActions.getProducts,
    ciActions.syncProducts
  ]);
  const [getTicketTemplates] = useActions([ticketTemplatesActions.getTicketTemplates]);

  const role = admin?.role;
  const isAdmin = role === 'ClientAdmin';
  const isGlobalAdmin = role === 'GlobalAdmin';
  const canEditAdminAndGA = isAdmin || isGlobalAdmin;
  const canEditGA = isGlobalAdmin;
  const classes = useStyles();

  const [templateOverlayOpen, setTemplateOverlayOpen] = useState(false);
  const [pageData, setPageData] = useState<ProductsPageData>({
    environmentId: null,
    orderTemplateId: null,
    products: []
  });
  const [productOverlayOpen, setProductOverlayOpen] = useState(false);
  const [priceTypeOverlayOpen, setPriceTypeOverlayOpen] = useState(false);
  const [productOverlayMode, setProductOverlayMode] = useState<'create' | 'edit'>('create');
  const [priceTypeOverlayMode] = useState<'create' | 'edit'>('create');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedPriceType] = useState<PriceType | null>(null);
  const [resetData, setResetData] = useState<ProductsPageData | null>(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const sortedTemplates = getSortedList(templates, 'type', 'name');
  const kisTechnologiesIntegrations = clientIntegrations?.filter((ci) => ci.integration?.code === 'kistechnologies');
  const sortedEnvironments = kisTechnologiesIntegrations?.sort((a, b) => a.name.localeCompare(b.name));
  const orderTemplates = sortedTemplates.filter((t) => t.type === 'receipt');
  const productTemplates = sortedTemplates.filter((t) => t.type === 'receipt' || t.type === 'sale');
  const orderTemplateOptions: (TicketTemplate | NullTicketTemplate)[] = [NULL_TICKET_TEMPLATE, ...orderTemplates];

  useEffect(() => {
    getClientIntegrations();
    getTicketTemplates();
  }, []);

  useEffect(() => {
    if (!pageData.environmentId && sortedEnvironments?.length) {
      setPageData((prev) => ({
        ...prev,
        environmentId: sortedEnvironments[0].id
      }));
    }
  }, [sortedEnvironments]);

  useEffect(() => {
    if (pageData.environmentId) {
      getProducts(pageData.environmentId);
      getPaymentSettings(pageData.environmentId);
    }
  }, [pageData.environmentId]);

  useEffect(() => {
    if (productsData && pageData.environmentId) {
      const mapped = mapApiToPageData(productsData, pageData.environmentId);
      setPageData(mapped);
      setResetData((prev) => prev ?? mapped);
    }
  }, [productsData]);

  useEffect(() => {
    if (!resetData) return;

    const isEqual = JSON.stringify(pageData) === JSON.stringify(resetData);
    setIsDisabled(isEqual);
    setHasUnsavedChanges(!isEqual);
  }, [pageData, resetData]);

  const refreshTemplates = () => {
    getTicketTemplates();
  };

  const isSaveDisabled = isDisabled || !pageData.environmentId || pageData.products.length === 0;

  const permissions = {
    product: {
      create: canEditAdminAndGA,
      edit: canEditAdminAndGA,
      delete: canEditAdminAndGA,
      toggle: canEditAdminAndGA,
      save: canEditAdminAndGA,
      editFee: canEditGA
    },
    priceType: {
      create: canEditAdminAndGA,
      edit: canEditAdminAndGA,
      delete: canEditAdminAndGA,
      toggle: canEditAdminAndGA
    },
    template: {
      manage: canEditAdminAndGA
    },
    page: {
      reset: canEditAdminAndGA,
      save: canEditAdminAndGA
    }
  };

  const productPermissions: ProductPermissions = {
    canEditProduct: permissions.product.edit,
    canSaveProduct: permissions.product.save,
    canCreatePriceType: permissions.priceType.create,
    canEditPriceType: permissions.priceType.edit,
    canDeletePriceType: permissions.priceType.delete,
    canTogglePriceType: permissions.priceType.toggle,
    canEditKisFee: permissions.product.editFee,
    canManageTemplates: permissions.template.manage
  };

  const mapApiToPageData = (data: ProductsApiResponse, environmentId: string): ProductsPageData => {
    return {
      environmentId,
      orderTemplateId: data.orderTicketTemplateId,
      products: data.products.map((p: any) => ({
        id: p.id,
        name: p.name,
        ticketTemplateId: p.ticketTemplateId ?? null,
        kisFeeAmount: p.fees?.[0]?.amount ?? null,
        kisFeePercent: toPercentage(p.fees?.[0]?.percentage),
        enabled: p.enabled,
        priceTypes: p.priceTypes.map((pt: any) => ({
          id: pt.id,
          name: pt.name,
          price: pt.price,
          ticketTemplateId: pt.ticketTemplateId ?? null,
          enabled: pt.enabled
        }))
      }))
    };
  };

  const isGuid = (id?: string) => {
    return id && id.length === 36;
  };

  const buildProductsPayload = (data: ProductsPageData) => {
    return {
      orderTicketTemplateId: data.orderTemplateId,
      products: data.products.map((p: Product) => ({
        ...(isGuid(p.id) ? { id: p.id } : {}),
        name: p.name,
        ticketTemplateId: p.ticketTemplateId,
        enabled: p.enabled,
        fees: [
          {
            amount: p.kisFeeAmount ?? null,
            percentage: toDecimal(p.kisFeePercent)
          }
        ],
        priceTypes: p.priceTypes.map((pt: PriceType) => ({
          ...(isGuid(pt.id) ? { id: pt.id } : {}),
          name: pt.name,
          price: pt.price,
          ticketTemplateId: pt.ticketTemplateId,
          enabled: pt.enabled
        }))
      }))
    };
  };

  const handleSave = () => {
    if (!pageData.environmentId) return;

    const payload = buildProductsPayload(pageData);

    syncProducts({
      id: pageData.environmentId,
      data: payload
    });

    setResetData(pageData);
    setHasUnsavedChanges(false);
  };

  // Save product
  const handleSaveProduct = (product: Product) => {
    setPageData((prev) => {
      let updatedProducts;

      if (productOverlayMode === 'create') {
        updatedProducts = [...prev.products, { ...product }];
      } else {
        updatedProducts = prev.products.map((p) => (p.id === product.id ? product : p));
      }

      return { ...prev, products: updatedProducts };
    });

    setProductOverlayOpen(false);
  };

  const handleDeleteProduct = () => {
    if (!productToDelete) return;

    setPageData((prev) => ({
      ...prev,
      products: prev.products.filter((p) => p.id !== productToDelete.id)
    }));

    setDeleteOverlayOpen(false);
    setProductToDelete(null);
  };

  const handleReset = () => {
    if (resetData) {
      setPageData(resetData);
    }
  };

  return (
    <>
      {/* Header */}
      <Grid className={classes.headerBlock}>
        <Box flexOverride="none">
          <Typography className={classes.heading}>Inventory</Typography>
        </Box>
        <MuiBox display="flex" alignItems="end" gap="10px">
          <Typography sx={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>Environment</Typography>
          <SelectDropdown
            items={sortedEnvironments || []}
            itemToString={(item) => item?.name || ''}
            selectedItem={sortedEnvironments?.find((e) => e.id === pageData.environmentId) || null}
            onChange={(item) => {
              setPageData((prev) => ({
                ...prev,
                environmentId: item?.id || null
              }));
            }}
            width="262px"
            radius="round"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: '#000'
              }
            }}
          />
        </MuiBox>
      </Grid>
      {/* Page Container */}
      <MuiBox padding="0 40px 40px 40px" className={classes.outerBox}>
        <Box
          raised
          width="100%"
          minHeight="572px"
          padding="24px"
          sx={{ borderRadius: '4px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}
        >
          {/* Order Template Dropdown */}
          <MuiBox className={classes.formBox}>
            <Typography
              className={classes.formLabel}
              sx={{ display: 'flex', justifyContent: 'space-between', width: '262px' }}
            >
              Order Template{' '}
              {canEditAdminAndGA && (
                <Tooltip title={'Manage Templates'} placement="top">
                  <Icon
                    name="Settings"
                    sx={{ cursor: 'pointer', mr: '10px' }}
                    onClick={() => {
                      setTemplateOverlayOpen(true);
                    }}
                  />
                </Tooltip>
              )}
            </Typography>
            <SelectDropdown
              items={orderTemplateOptions}
              itemToString={(item: TicketTemplate | NullTicketTemplate) =>
                `${item.name}${item.type ? ` (${item.type})` : ''}`
              }
              selectedItem={orderTemplateOptions.find((t) => t.id === pageData.orderTemplateId) || NULL_TICKET_TEMPLATE}
              onChange={(item: TicketTemplate | NullTicketTemplate) => {
                setPageData((prev) => ({
                  ...prev,
                  orderTemplateId: item?.id ?? null
                }));
              }}
              width="262px"
              radius="round"
              sx={{
                boxShadow: 'none',
                height: '45px',
                '& .MuiSelect-select': {
                  color: '#000'
                }
              }}
              disabled={!canEditAdminAndGA}
            />
          </MuiBox>

          {/* Products Table */}
          <ProductsTable
            products={pageData.products}
            onCreate={() => {
              setProductOverlayMode('create');
              setSelectedProduct(null);
              setProductOverlayOpen(true);
            }}
            onEdit={(product: Product) => {
              setProductOverlayMode('edit');
              setSelectedProduct(product);
              setProductOverlayOpen(true);
            }}
            onDelete={(product: Product) => {
              setProductToDelete(product);
              setDeleteOverlayOpen(true);
            }}
            onToggle={(product: Product) => {
              setPageData((prev) => ({
                ...prev,
                products: prev.products.map((p) => (p.id === product.id ? { ...p, enabled: !p.enabled } : p))
              }));
            }}
            templates={productTemplates}
            permissions={permissions.product}
          />
        </Box>

        {/* Reset / Save Buttons (UI only for now) */}
        <Box className={classes.buttonGroup}>
          <Button
            customSize="small"
            width={144}
            onClick={handleReset}
            disabled={isDisabled || !permissions.page.reset || !canEditAdminAndGA}
          >
            Reset
          </Button>
          <Button
            customSize="small"
            width={144}
            onClick={handleSave}
            disabled={isSaveDisabled || !permissions.page.save || !canEditAdminAndGA}
          >
            Save
          </Button>
        </Box>

        {/* Product Overlay */}
        {productOverlayOpen && (
          <ProductOverlay
            mode={productOverlayMode}
            initialData={selectedProduct}
            onClose={() => setProductOverlayOpen(false)}
            onSave={handleSaveProduct}
            templates={productTemplates}
            onTemplatesUpdated={refreshTemplates}
            permissions={productPermissions}
            defaultKisProductFee={paymentSettings?.defaultKisProductFee}
          />
        )}

        {/* Price Type Overlay */}
        {priceTypeOverlayOpen && (
          <PriceTypeOverlay
            mode={priceTypeOverlayMode}
            initialData={selectedPriceType}
            onClose={() => setPriceTypeOverlayOpen(false)}
            onSave={(priceType) => {
              setPageData((prev) => ({
                ...prev,
                products: prev.products.map((p) => {
                  if (p.id === selectedProduct?.id) {
                    return {
                      ...p,
                      priceTypes: [...p.priceTypes, priceType]
                    };
                  }
                  return p;
                })
              }));

              setPriceTypeOverlayOpen(false);
            }}
            templates={productTemplates}
            onTemplatesUpdated={refreshTemplates}
            permissions={productPermissions}
          />
        )}
      </MuiBox>
      {templateOverlayOpen && (
        <KioskTemplateOverlay
          canEdit={permissions.template.manage}
          onClose={() => {
            setTemplateOverlayOpen(false);
            getTicketTemplates(); // refresh templates after closing
          }}
        />
      )}
      {deleteOverlayOpen && (
        <FormDialog
          open={deleteOverlayOpen}
          confirmButtonAction={handleDeleteProduct}
          confirmButtonText="Delete"
          cancelButtonAction={() => {
            setDeleteOverlayOpen(false);
            setProductToDelete(null);
          }}
          cancelButtonText="Cancel"
        >
          <MuiBox>
            <Typography fontSize={24}>Are you sure you want to delete this product?</Typography>
          </MuiBox>
        </FormDialog>
      )}
    </>
  );
};

export default ProductsView;
